import { useCallback, useContext, useMemo } from 'react';
import useApiCall from 'hooks/use-api-call';
import useInvestorMe from 'hooks/use-investor-me';
import useInvestmentInvitation from 'subapps/investment/pages/investment/hooks/use-investment-invitation';
import { DocumentsApi, InvestorsApi, PatchedInvestorUpdateRequest } from 'api';
import WizardContext from 'libraries/wizard/wizard-context';
import { AnyServerError } from 'hooks/use-api-error';
import useInvestment from 'hooks/use-investment';
import { TaxInformationFields } from 'libraries/wizard/components/tax-information/tax-information-form';

const useTaxInformation = (): {
  onSubmit: (values: TaxInformationFields) => void;
  showLoading: boolean;
  error?: AnyServerError;
} => {
  const { investmentId, resourceId: invitationId, loading, finalize } = useContext(WizardContext);

  const { error: apiError, loading: apiLoading, makeAuthenticatedApi, withApi } = useApiCall();

  const { investor, loading: investorLoading, error: meError } = useInvestorMe();

  const {
    token: tokenInvitation,
    error: invitationError,
    loading: loadingInvitation,
    invitation,
  } = useInvestmentInvitation(invitationId);
  const { token: tokenInvestment } = useInvestment(investmentId);

  const token = invitation ? tokenInvitation : tokenInvestment;

  const showLoading = !token || loading || loadingInvitation || investorLoading || apiLoading;
  const error = invitationError || apiError || meError;

  const investorsApi: InvestorsApi = useMemo(() => makeAuthenticatedApi(InvestorsApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const onSubmit = useCallback(
    (fieldsValues: TaxInformationFields) => {
      withApi(async () => {
        if (!investor?.id) return false;

        const params: PatchedInvestorUpdateRequest = {
          taxInformation: {
            isSubjectToGermanTax: fieldsValues.isSubjectToGermanTax,
            taxIdentificationNumber: fieldsValues.taxIdentificationNumber,
            nonAssessmentCertificate: fieldsValues.hasNonAssessmentCertificate,
          },
        };

        if (fieldsValues.nonAssessmentCertificate) {
          const docResponse = await documentsApi.documentsCreate({
            file: fieldsValues.nonAssessmentCertificate?.file!,
          });
          params.taxInformation!.nonAssessmentCertificateDocId = docResponse.id;
        }
        await investorsApi.investorsPartialUpdate({
          id: investor.id,
          patchedInvestorUpdateRequest: params,
        });

        finalize();
      });
    },
    [investor?.id, investorsApi, documentsApi, withApi, finalize],
  );

  return useMemo(
    () => ({
      showLoading,
      error,
      onSubmit,
    }),
    [onSubmit, investmentId, invitationId, showLoading, error],
  );
};

export default useTaxInformation;
